/**
 * HelpCallout — explanatory panel that appears at the top of a wizard
 * step or drawer.
 *
 * Three blocks (any can be omitted):
 *   • whatHappens — one-paragraph explanation of what clicking submit does
 *   • youNeed     — bullet list of pre-requisites
 *   • afterwards  — bullet list of follow-up steps the user should expect
 *
 * Collapsible: defaults to collapsed, click the header to expand.
 * Per-page collapsed state persists in localStorage so once a user
 * dismisses the help on a page, they don't see it expanded again.
 */
import { useEffect, useState, type JSX, type ReactNode } from 'react';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';

interface HelpCalloutProps {
  readonly title?: string;
  readonly whatHappens?: ReactNode;
  readonly youNeed?: readonly ReactNode[];
  readonly afterwards?: readonly ReactNode[];
  /** Used for the localStorage key. Defaults to title. */
  readonly storageKey?: string;
  /** First-render default before the user toggles. */
  readonly defaultOpen?: boolean;
}

export function HelpCallout({
  title = 'What this does',
  whatHappens,
  youNeed,
  afterwards,
  storageKey,
  defaultOpen = false,
}: HelpCalloutProps): JSX.Element {
  const key = `sumcp_help_${storageKey ?? title}`;
  const [open, setOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return defaultOpen;
    const v = window.localStorage.getItem(key);
    if (v === '1') return true;
    if (v === '0') return false;
    return defaultOpen;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(key, open ? '1' : '0');
  }, [key, open]);

  return (
    <aside
      style={{
        border: '1px solid rgba(70, 100, 140, 0.2)',
        background: 'rgba(70, 100, 140, 0.06)',
        borderRadius: 10,
        padding: open ? 14 : '8px 12px',
        fontSize: 13.5,
        lineHeight: 1.55,
        color: 'inherit',
      }}
      aria-label={title}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: open ? 8 : 0,
          fontWeight: 700,
          color: 'rgb(40, 70, 110)',
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          fontSize: 13.5,
        }}
      >
        {open ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
        <Info size={14} aria-hidden /> {title}
        {!open && (
          <span style={{ fontWeight: 400, opacity: 0.7, marginLeft: 4 }}>(click to expand)</span>
        )}
      </button>
      {open && (
        <>
          {whatHappens && (
            <p style={{ margin: '0 0 8px' }}>
              <strong>What happens: </strong>
              {whatHappens}
            </p>
          )}
          {youNeed && youNeed.length > 0 && (
            <div style={{ marginBottom: afterwards && afterwards.length > 0 ? 8 : 0 }}>
              <strong>What you need:</strong>
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {youNeed.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          )}
          {afterwards && afterwards.length > 0 && (
            <div>
              <strong>After this is done:</strong>
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {afterwards.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </aside>
  );
}
